import React from 'react';
import { Controller } from 'react-hook-form';
import { IconArrowRight } from '@tabler/icons-react';
import { Link } from '@planne-software/uni';
import { Button } from '@planne-software/uni/Button';
import { InputLabel } from '@planne-software/uni/Input';
import { Stack } from '@planne-software/uni/mui/material';

import { PinCodeInput } from '@src/common/ui/PinCodeInput';
import { useTranslate } from '@src/core/hooks';
import { VERIFICATION_CODE_LENGTH } from '@src/domains/customers';
import {
  AuthCardButtonWrapper,
  AuthCardRoot,
  AuthCardSubtitle,
  AuthCardTextGroup,
  AuthCardTitle,
  authLinkSx,
  ButtonGroup,
  OtpTimerText,
  RecoveryLinkPrefix,
  RecoveryLinkRow,
} from '@src/features/customers';

import { useRecoveryPinForm } from '../hooks/useRecoveryPinForm';

type PasswordRecoveryPinCardProps = {
  onBack: () => void;
  onContinue: () => void;
};

export const PasswordRecoveryPinCard = (props: PasswordRecoveryPinCardProps) => {
  const { onBack, onContinue } = props;
  const { i18n, i18nElement } = useTranslate();

  const {
    control,
    loading,
    isSubmitEnabled,
    recoveryEmail,
    status,
    message,
    onSubmit,
    resendCode,
    showResendCountdown,
    showResendLink,
    resendSeconds,
    handleCodeChange,
  } = useRecoveryPinForm({ onContinue });

  return (
    <AuthCardRoot data-testid='password-recovery-pin-card'>
      <AuthCardTextGroup>
        <AuthCardTitle>{i18n('checkout_auth_recovery_title')}</AuthCardTitle>
        <AuthCardSubtitle>
          {i18nElement('checkout_auth_recovery_otp_subtitle', { email: recoveryEmail ?? '' })}
        </AuthCardSubtitle>
      </AuthCardTextGroup>

      <form onSubmit={onSubmit} noValidate>
        <AuthCardRoot>
          <Stack gap={1}>
            <InputLabel error={status === 'error'} success={status === 'success'} size='medium'>
              {i18n('checkout_auth_otp_code_label')}
            </InputLabel>
            <Controller
              name='code'
              control={control}
              render={({ field: { name, ...rest } }) => (
                <PinCodeInput
                  value={rest.value}
                  onChange={handleCodeChange}
                  name={name}
                  mode='alphanumeric'
                  length={VERIFICATION_CODE_LENGTH}
                  disabled={loading}
                  status={status}
                  message={message}
                />
              )}
            />
          </Stack>

          {showResendCountdown || showResendLink ? (
            <RecoveryLinkRow>
              <RecoveryLinkPrefix>{i18n('checkout_auth_did_not_receive_email')}</RecoveryLinkPrefix>
              {showResendLink ? (
                <Link
                  component='button'
                  type='button'
                  underline='none'
                  onClick={resendCode}
                  disabled={loading}
                  sx={authLinkSx}
                >
                  {i18n('checkout_auth_resend_email_link')}
                </Link>
              ) : (
                <OtpTimerText>{i18n('checkout_auth_otp_resend_countdown', { seconds: resendSeconds })}</OtpTimerText>
              )}
            </RecoveryLinkRow>
          ) : null}

          <ButtonGroup>
            <AuthCardButtonWrapper>
              <Button
                type='submit'
                fullWidth
                loading={loading}
                disabled={!isSubmitEnabled}
                endSlot={<IconArrowRight size={20} aria-hidden />}
              >
                {i18n('checkout_auth_continue_cta')}
              </Button>
            </AuthCardButtonWrapper>

            <AuthCardButtonWrapper>
              <Button type='button' variant='slim' fullWidth disabled={loading} onClick={onBack}>
                {i18n('checkout_auth_back_cta')}
              </Button>
            </AuthCardButtonWrapper>
          </ButtonGroup>
        </AuthCardRoot>
      </form>
    </AuthCardRoot>
  );
};

export default PasswordRecoveryPinCard;
